/**
 * InstallMomentTrigger — bumps the session counter and re-broadcasts high
 * dopamine moments as `install-prompt:arm` so the install hint cards can
 * surface at the right time.
 *
 * Listens for:
 *   - goal:completed
 *   - certificate:generated
 *   - streak:milestone
 *   - points-awarded (only when detail.milestone === true)
 *
 * Mounted once at the App root.
 */
import { useEffect } from "react";
import { bumpSession } from "@/lib/installPromptState";

export function InstallMomentTrigger() {
  useEffect(() => {
    bumpSession();

    const arm = (reason: string) => {
      try {
        window.dispatchEvent(new CustomEvent("install-prompt:arm", { detail: { reason } }));
      } catch {/* noop */}
    };

    const onGoalCompleted = () => arm("goal_completed");
    const onCertificate = () => arm("certificate_generated");
    const onStreak = () => arm("streak_milestone");
    const onPointsAwarded = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.milestone) arm("milestone_points");
    };

    window.addEventListener("goal:completed", onGoalCompleted);
    window.addEventListener("certificate:generated", onCertificate);
    window.addEventListener("streak:milestone", onStreak);
    window.addEventListener("points-awarded", onPointsAwarded as EventListener);

    return () => {
      window.removeEventListener("goal:completed", onGoalCompleted);
      window.removeEventListener("certificate:generated", onCertificate);
      window.removeEventListener("streak:milestone", onStreak);
      window.removeEventListener("points-awarded", onPointsAwarded as EventListener);
    };
  }, []);

  return null;
}
